import {
  useRef, useState, useMemo, useCallback, useEffect
} from 'react'
import {
  Stage, Text, Label, Tag, Layer, Shape, Line, Rect, Circle, Group
} from 'react-konva'
import {Box} from '@binance/uikit'
import {formatNumber} from 'utils/format'
import useDepth from 'hooks/useDepth'
import useRouter from 'hooks/useRouter'
import useSymbolInfo from 'hooks/useSymbolInfo'

const STROKE_WIDTH = 1
const GRAPH_STROKE_WIDTH = 2
const MASK_OPACITY = 0.45
const AXIS_Y_WIDTH = 60
const BG_COLOR = '#12161C'

export default function DepthChart (props) {
  const containerRef = useRef(null)
  const [size, setSize] = useState({width: 200, height: 200})
  const router = useRouter()
  const {symbol} = router.query

  const stageRef = useRef(null)
  const {scale} = props

  const {symbolInfo} = useSymbolInfo()

  const pricePrecision = symbolInfo?.pricePrecision

  const [cursor, setCursor] = useState({
    x: null,
    y: null
  })
  const handleMouseMove = useCallback(() => {
    setCursor(stageRef.current.getPointerPosition())
  }, [])

  const handleMouseLeave = useCallback(() => {
    setCursor({x: null, y: null})
  }, [])

  const canvasWidth = size?.width
  const canvasHeight = size?.height
  const canvasHalfHeight = size?.height / 2
  const chartWidth = canvasWidth - AXIS_Y_WIDTH

  const {
    asks,
    bids,
    pricePos,
    depthPos,
    askX,
    askY,
    bidX,
    bidY,
    currentAsk,
    currentBid,
    scaleArray
  } = useDepth(
    symbol,
    canvasHeight,
    chartWidth,
    scale,
    {x: canvasHeight - cursor.y, y: cursor.x},
    true
  )

  const bidColor = '#00c582'
  const askColor = '#ff0372'
  const bidFillColor = 'rgba(46, 189, 133, 0.2)'
  const askFillColor = 'rgba(242, 48, 81, 0.2)'

  useEffect(() => {
    function handleResize () {
      const {width, height} = containerRef?.current?.getBoundingClientRect()
      setSize({width, height})
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return useMemo(
    () => (
      <Box display={props.hidden ? 'none' : 'block'} style={{height: '100%'}} ref={containerRef}>
        <Stage
          ref={stageRef}
          width={canvasWidth}
          height={canvasHeight}
          onMouseMove={handleMouseMove}
          onMouseOut={handleMouseLeave}
        >
          <Layer hitGraphEnabled={false}>
            <Group x={AXIS_Y_WIDTH}>
              <Line
                points={[0, canvasHalfHeight, chartWidth, canvasHalfHeight]}
                stroke="rgba(100, 100, 100, 0.4)"
                strokeWidth={STROKE_WIDTH}
              />
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath()
                  let x = chartWidth
                  let y = canvasHalfHeight
                  let firstDraw = true

                  bids.forEach(({price, depth}) => {
                    y = canvasHeight - pricePos(price)
                    if (y > canvasHeight || y < canvasHalfHeight) return
                    const nextX = depthPos(depth)
                    if (firstDraw) {
                      context.moveTo(x, y)
                      firstDraw = false
                    } else {
                      context.lineTo(x, y)
                    }
                    x = nextX
                    context.lineTo(x, y)
                  })
                  context.lineTo(x, canvasHeight + STROKE_WIDTH)
                  context.lineTo(chartWidth + STROKE_WIDTH, canvasHeight + STROKE_WIDTH)
                  // (!) Konva specific method, it is very important
                  context.fillStrokeShape(shape)
                }}
                fill={bidFillColor}
                stroke={bidColor}
                strokeWidth={GRAPH_STROKE_WIDTH}
              />
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath()
                  let x = chartWidth
                  let y = canvasHalfHeight
                  let firstDraw = false

                  asks.forEach(({price, depth}) => {
                    y = canvasHeight - pricePos(price)
                    if (y < 0 || y > canvasHalfHeight) return
                    const nextX = depthPos(depth)
                    if (firstDraw) {
                      context.moveTo(x, y)
                      firstDraw = false
                    } else {
                      context.lineTo(x, y)
                    }
                    x = nextX
                    context.lineTo(x, y)
                  })
                  context.lineTo(x, -STROKE_WIDTH)
                  context.lineTo(chartWidth, -STROKE_WIDTH)
                  // (!) Konva specific method, it is very important
                  context.fillStrokeShape(shape)
                }}
                fill={askFillColor}
                stroke={askColor}
                strokeWidth={GRAPH_STROKE_WIDTH}
              />
              {askX ? (
                <>
                  <Rect
                    x={0}
                    y={0}
                    width={chartWidth}
                    height={canvasHeight - askX}
                    fill="#000"
                    opacity={MASK_OPACITY}
                  />
                  <Rect
                    x={0}
                    y={canvasHeight - bidX}
                    width={chartWidth}
                    height={bidX}
                    fill="#000"
                    opacity={MASK_OPACITY}
                  />
                  <Line
                    points={[0, canvasHeight - askX, chartWidth, canvasHeight - askX]}
                    stroke={askColor}
                    strokeWidth={STROKE_WIDTH}
                    dash={[2, 2]}
                  />
                  <Line
                    points={[askY, 0, askY, canvasHalfHeight]}
                    stroke={askColor}
                    strokeWidth={STROKE_WIDTH}
                    dash={[2, 2]}
                  />
                  <Line
                    points={[0, canvasHeight - bidX, chartWidth, canvasHeight - bidX]}
                    stroke={bidColor}
                    strokeWidth={STROKE_WIDTH}
                    dash={[2, 2]}
                  />
                  <Line
                    points={[bidY, canvasHalfHeight, bidY, canvasHeight]}
                    stroke={bidColor}
                    strokeWidth={STROKE_WIDTH}
                    dash={[2, 2]}
                  />
                  <Circle x={askY} y={canvasHeight - askX} radius={3} fill={askColor} />
                  <Circle x={bidY} y={canvasHeight - bidX} radius={3} fill={bidColor} />
                  <Label x={askY} y={0}>
                    <Tag
                      pointerDirection="up"
                      pointerWidth={4}
                      pointerHeight={2}
                      stroke={askColor}
                      strokeWidth={STROKE_WIDTH}
                      fill={BG_COLOR}
                    />
                    <Text text={currentAsk?.depth} fill={askColor} padding={3} />
                  </Label>
                  <Label x={bidY} y={canvasHeight}>
                    <Tag
                      pointerDirection="down"
                      pointerWidth={4}
                      pointerHeight={2}
                      stroke={bidColor}
                      strokeWidth={STROKE_WIDTH}
                      fill={BG_COLOR}
                    />
                    <Text text={currentBid?.depth} fill={bidColor} padding={3} />
                  </Label>
                </>
              ) : null}
            </Group>
            <Group>
              <Line
                points={[AXIS_Y_WIDTH, 0, AXIS_Y_WIDTH, canvasHeight]}
                stroke="rgba(100, 100, 100, 0.4)"
                strokeWidth={STROKE_WIDTH}
              />
              {scaleArray.map((price, idx) => {
                const posY = canvasHeight - pricePos(price)
                return (
                  <React.Fragment key={idx}>
                    <Line
                      points={[AXIS_Y_WIDTH - 4, posY, AXIS_Y_WIDTH, posY]}
                      stroke="#fff"
                      strokeWidth={STROKE_WIDTH}
                    />
                    <Label x={AXIS_Y_WIDTH - 4} y={posY}>
                      <Tag
                        pointerDirection="right"
                        pointerWidth={0}
                        pointerHeight={0}
                        strokeWidth={0}
                      />
                      <Text text={formatNumber(price, pricePrecision)} fill={'#999'} padding={3} />
                    </Label>
                  </React.Fragment>
                )
              })}
              {askX ? (
                <>
                  <Label x={AXIS_Y_WIDTH} y={canvasHeight - askX}>
                    <Tag
                      pointerDirection="right"
                      pointerWidth={9}
                      pointerHeight={18}
                      stroke={askColor}
                      strokeWidth={STROKE_WIDTH}
                      fill={BG_COLOR}
                    />
                    <Text text={currentAsk?.price} fill={askColor} padding={3} />
                  </Label>
                  <Label x={AXIS_Y_WIDTH} y={canvasHeight - bidX}>
                    <Tag
                      pointerDirection="right"
                      pointerWidth={9}
                      pointerHeight={18}
                      stroke={bidColor}
                      strokeWidth={STROKE_WIDTH}
                      fill={BG_COLOR}
                    />
                    <Text text={currentBid?.price} fill={bidColor} padding={3} />
                  </Label>
                </>
              ) : null}
            </Group>
          </Layer>
        </Stage>
      </Box>
    ),
    [
      props.hidden,
      canvasWidth,
      canvasHeight,
      handleMouseMove,
      handleMouseLeave,
      canvasHalfHeight,
      chartWidth,
      askX,
      bidX,
      askY,
      bidY,
      currentAsk,
      currentBid,
      scaleArray,
      bids,
      pricePos,
      depthPos,
      asks,
      pricePrecision
    ]
  )
}
